import { useState } from "react";
import { useCustomer } from "@/lib/customer-context";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { VideoKycDialog } from "@/components/video-kyc-dialog";
import { SetPinModal } from "@/components/customer/set-pin-modal";
import { useToast } from "@/hooks/use-toast";
import {
  User,
  Mail,
  Phone,
  Camera,
  Shield,
  Bell,
  CreditCard,
  CheckCircle2,
  Loader2,
  KeyRound,
} from "lucide-react";

export default function PortalAccount() {
  const { customer, refreshCustomer } = useCustomer();
  const { toast } = useToast();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [videoKycOpen, setVideoKycOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const startEditing = () => {
    setEditForm({
      firstName: customer?.firstName || "",
      lastName: customer?.lastName || "",
      email: customer?.email || "",
    });
    setIsEditingProfile(true);
  };

  const cancelEditing = () => {
    setIsEditingProfile(false);
    setEditForm({ firstName: "", lastName: "", email: "" });
  };

  const saveProfile = async () => {
    setIsSaving(true);
    try {
      const response = await apiRequest("PATCH", "/api/customer/profile", editForm);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to update profile");
      }
      await refreshCustomer();
      setIsEditingProfile(false);
      toast({ title: "Success", description: "Profile updated successfully" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = () => {
    if (customer?.firstName && customer?.lastName) {
      return `${customer.firstName[0]}${customer.lastName[0]}`.toUpperCase();
    }
    if (customer?.firstName) {
      return customer.firstName.slice(0, 2).toUpperCase();
    }
    return "CU";
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">My Account</h1>
        <p className="text-muted-foreground mt-1">
          Manage your profile and account settings
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar */}
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  {customer?.avatarUrl && <AvatarImage src={customer.avatarUrl} />}
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <Button variant="outline" size="sm">
                    <Camera className="h-4 w-4 mr-2" />
                    Change Photo
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG or GIF. Max 2MB.
                  </p>
                </div>
              </div>

              {/* Profile Form */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={isEditingProfile ? editForm.firstName : (customer?.firstName || "")}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    disabled={!isEditingProfile}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={isEditingProfile ? editForm.lastName : (customer?.lastName || "")}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    disabled={!isEditingProfile}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={isEditingProfile ? editForm.email : (customer?.email || "")}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="pl-10"
                      disabled={!isEditingProfile}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      value={customer?.phone || ""}
                      className="pl-10"
                      disabled
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Phone number cannot be changed
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                {isEditingProfile ? (
                  <>
                    <Button onClick={saveProfile} disabled={isSaving}>
                      {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={cancelEditing} disabled={isSaving}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button onClick={startEditing}>
                    Edit Profile
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>KYC Verification</CardTitle>
              <CardDescription>Your identity verification status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    customer?.kycStatus === "VERIFIED"
                      ? "bg-green-500/10"
                      : customer?.kycStatus === "REJECTED"
                      ? "bg-red-500/10"
                      : "bg-primary/10"
                  }`}>
                    {customer?.kycStatus === "VERIFIED" ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Shield className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">KYC Status</p>
                    <p className="text-sm text-muted-foreground">
                      {customer?.kycStatus === "VERIFIED"
                        ? "Your identity has been verified"
                        : customer?.kycStatus === "IN_PROGRESS"
                        ? "Verification in progress"
                        : customer?.kycStatus === "PENDING_REVIEW"
                        ? "Submitted and awaiting review"
                        : customer?.kycStatus === "REJECTED"
                        ? "Verification was rejected — please try again"
                        : "Complete verification to unlock all features"}
                    </p>
                  </div>
                </div>
                <Badge
                  variant={customer?.kycStatus === "VERIFIED" ? "default" : customer?.kycStatus === "REJECTED" ? "destructive" : "outline"}
                  className={customer?.kycStatus === "VERIFIED" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : ""}
                >
                  {(customer?.kycStatus || "NOT_STARTED").replace("_", " ")}
                </Badge>
              </div>

              {/* KYC Action Buttons */}
              {customer?.kycStatus !== "VERIFIED" && (
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setVideoKycOpen(true)}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Start KYC
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* KYC Dialog */}
          {customer && (
            <>
              <VideoKycDialog
                open={videoKycOpen}
                onOpenChange={setVideoKycOpen}
                customerId={customer.id}
                customerName={customer.firstName && customer.lastName ? `${customer.firstName} ${customer.lastName}` : undefined}
                customerEmail={customer.email || undefined}
                customerPhone={customer.phone}
                onSuccess={() => refreshCustomer()}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Two-Factor Authentication</p>
                    <p className="text-sm text-muted-foreground">
                      SMS one-time code on every sign-in
                    </p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Always On
                </Badge>
              </div>

              <div className="border-t" />

              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <KeyRound className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Portal PIN</p>
                    <p className="text-sm text-muted-foreground">
                      {customer?.pinSetupRequired
                        ? "Not set — choose a 6-digit PIN as a second factor"
                        : "6-digit PIN required after SMS code on every sign-in"}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPinModalOpen(true)}>
                  {customer?.pinSetupRequired ? "Set PIN" : "Change PIN"}
                </Button>
              </div>
              {!customer?.pinSetupRequired && (
                <p className="text-xs text-muted-foreground pt-1">
                  Forgot your PIN? Contact support to have it reset.
                </p>
              )}
            </CardContent>
          </Card>

          <SetPinModal
            open={pinModalOpen}
            changeMode={!customer?.pinSetupRequired}
            onClose={() => setPinModalOpen(false)}
          />
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Choose what updates you receive</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Transaction Alerts</p>
                    <p className="text-sm text-muted-foreground">
                      Get notified for every transaction
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  Configure
                </Button>
              </div>

              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Card Status Updates</p>
                    <p className="text-sm text-muted-foreground">
                      Get notified about card changes
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  Configure
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
